/**
 * Bridges the parent window's scroll position into the embedded (pym) iframe.
 *
 * When the event page is hosted inside a parent site's pym iframe, the iframe is
 * sized to its full content height (scrolling="no"), so the iframe never scrolls —
 * the parent window owns the scroll. Anything that depends on the local scroll
 * position (sticky header, scroll spy, smooth scrolling) is therefore broken.
 *
 * `embeddedInit.js` (the parent-side bootstrap) already postMessages the parent's
 * scroll offset to the child as "parentScrollPosition:{left,top}", where `top` is
 * the y-offset within the iframe content that currently sits at the top of the
 * parent's visible viewport. This module listens for that and exposes it.
 */

let parentScrollTop = 0;
let attached = false;
const listeners = new Set();

const PREFIX = "parentScrollPosition:";

function handleMessage(e) {
  // Accept the scroll bridge from the embedder on any domain (open embedding),
  // but only from our direct parent window — this rejects sibling frames,
  // popups, or injected scripts trying to spoof a parentScrollPosition message
  // without restricting the origin.
  if (e.source !== window.parent) return;
  if (typeof e.data !== "string" || e.data.indexOf(PREFIX) !== 0) return;
  try {
    const data = JSON.parse(e.data.slice(PREFIX.length));
    if (data && typeof data.top === "number") {
      parentScrollTop = Math.max(0, data.top);
      listeners.forEach((cb) => cb(parentScrollTop));
    }
  } catch (_) {
    /* ignore malformed messages */
  }
}

function ensureListener() {
  if (attached || typeof window === "undefined") return;
  attached = true;
  window.addEventListener("message", handleMessage, false);
}

/**
 * True when the page is nested inside a parent frame. This is known immediately at
 * load (before pym initialises), so sticky/scroll-spy logic can rely on it from the
 * first render. The pym child bridge (needed to *drive* the parent scroll) loads via
 * an inline script after the bundle — gate scroll actions on canBridgeScroll() below.
 */
export const isEmbedded = () =>
  typeof window !== "undefined" && window.self !== window.top;

/** True once the pym child bridge is available to scroll the parent. */
const canBridgeScroll = () =>
  isEmbedded() &&
  !!window.pymChild &&
  typeof window.pymChild.scrollParentToChildPos === "function";

/** Latest known parent scroll offset (in child content coordinates). */
export const getParentScrollTop = () => parentScrollTop;

/** Subscribe to parent scroll updates. Returns an unsubscribe function. */
export const subscribeParentScroll = (cb) => {
  ensureListener();
  listeners.add(cb);
  return () => listeners.delete(cb);
};

/**
 * Smoothly scroll the parent window so the given child content offset lands at the
 * top of the parent's viewport. Animates child-side (easing over repeated
 * scrollParentToChildPos calls) because pym's parent handler scrolls instantly.
 */
export const smoothScrollParentTo = (to, duration = 400) => {
  if (!canBridgeScroll()) return false;
  ensureListener();

  const from = parentScrollTop;
  const distance = to - from;
  const clock = () =>
    typeof performance !== "undefined" && performance.now
      ? performance.now()
      : new Date().getTime();
  const startTime = clock();
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  const step = () => {
    const elapsed = Math.min(1, (clock() - startTime) / duration);
    const pos = Math.round(from + distance * easeOutCubic(elapsed));
    window.pymChild.scrollParentToChildPos(pos);
    if (elapsed < 1) window.requestAnimationFrame(step);
  };

  window.requestAnimationFrame(step);
  return true;
};

// Attach eagerly so the latest parent offset is available even before the first
// component subscribes (the parent may scroll during initial load).
ensureListener();

// Diagnostic: run window.__embeddedScrollDebug() in the iframe console while scrolling
// the parent to confirm the bridge is live and parentScrollTop is updating.
if (typeof window !== "undefined") {
  window.__embeddedScrollDebug = () => ({
    isEmbedded: isEmbedded(),
    canBridgeScroll: canBridgeScroll(),
    hasPym: !!window.pymChild,
    parentScrollTop,
  });
}
